import {
  PreloaderButtonState,
  PreloaderSubmitButton,
  Requirement
  , useWauth
} from "@monorepo/components";
import {
  Box,
  Column,
  GridContainer,
  Row,
  StandardForm,
  Typography,
  BoundedContent
} from "@vp/swan";
import { Auth0Error } from "auth0-js";
import React, { FormEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "../../useTranslation";
import { SignAuth0Context } from "../../Auth0Context";
import { useNotificationContext } from "../../NotificationContext";
import { useAuthenticationContext } from "../AuthenticationContext";
import EmailInput from "../Components/EmailInput";
import { Screens } from "../ScreensLayout";
import BackLink from "./BackButton";
import "./Reset.scss";

import { pushDataToSegment, saveLastAttemptInfo } from "../../segmentData";
import { PASSWORD_RESET_PAGE, PASSWORD_RESET_ATTEMPT } from "../../constant";

interface ResetProps {
  onChangeScreen: (screen: Screens) => void;
}

const Reset = (props: ResetProps) => {
  const [t] = useTranslation("reset");
  const { onChangeScreen } = props;

  const {
    contextEmail,
    setContextEmail,
    validateEmail
  } = useAuthenticationContext();
  const {
    resetEmailSent,
    resetEmailFailed,
    invalidFormSubmitted
  } = useNotificationContext();
  const auth0 = useContext(SignAuth0Context);
  const [email, setEmail] = useState({ value: contextEmail, isValid: true });
  const [submitState, setSubmitState] = useState(PreloaderButtonState.Disabled);
  const wauth = useWauth();

  const resetCallback = (err: Auth0Error | null): void => {
    onChangeScreen(Screens.SignIn);

    if (err) {
      if (err.code) {
        resetEmailFailed(err.code);
      } else {
        resetEmailFailed();
      }
    } else {
      resetEmailSent(auth0, email.value);
    }

    setSubmitState(PreloaderButtonState.Enabled);
  };

  const onResetSubmit = (e: FormEvent): void => {
    e.preventDefault();
    saveLastAttemptInfo({ label: "Auth0" });
    pushDataToSegment(PASSWORD_RESET_ATTEMPT, false, {
      pageName: PASSWORD_RESET_PAGE
    });

    if (submitState !== PreloaderButtonState.Enabled) {
      invalidFormSubmitted(PASSWORD_RESET_PAGE);
    } else {
      setSubmitState(PreloaderButtonState.Preloader);
      auth0.resetPassword(contextEmail, resetCallback);
    }
  };

  const onEmailChanged = (email: string, isValid: boolean): void => {
    if (isValid) {
      setContextEmail(email);
    }

    setEmail({ value: email, isValid });
  };

  useEffect(() => {
    if (email.isValid) {
      setSubmitState(PreloaderButtonState.Enabled);
    } else {
      setSubmitState(PreloaderButtonState.Disabled);
    }
  }, [email.isValid]);

  useEffect(() => {
    document.title = t("documentTitle");
  });

  useEffect(() => {
    pushDataToSegment(PASSWORD_RESET_PAGE, true);
  }, []);

  const renderContent = () => {
    return (
      <>
        <Box mb={6}>
          <BackLink onChangeScreen={onChangeScreen} />
        </Box>
        <Box mb="between-subsections">
          <Box mb={4}>
            <Typography fontSkin="title-headline" data-testid="header">
              {t("title")}
            </Typography>
          </Box>
          <Typography fontSkin="body-standard">{t("info") }</Typography>
        </Box>
        <StandardForm
          id="reset-password-form"
          skin="tight"
          data-testid="reset-password-form"
          method="post"
          onSubmit={onResetSubmit}
          noValidate
        >
          <Box mb={6}>
            <EmailInput
              disabled={Boolean(
                (wauth.enforcedEmail && wauth.isSteppedUp) ||
                (wauth.enforcedEmail && wauth.disableEmailField)
              )}
              id="reset-email"
              email={email.value}
              validateEmail={validateEmail}
              onEmailChanged={onEmailChanged}
              wrappedProps={{ my: "m" }}
              required={Requirement.Required}
              autoComplete={"Email"}
            />
          </Box>
          <PreloaderSubmitButton
            name="submit"
            wrappedProps={{ my: "m" }}
            text={t("submit.text")}
            preloaderText={t("submit.preloaderText")}
            preloaderAccessibilityLabel={t(
              "submit.preloaderAccessibilityLabel"
            )}
            state={submitState}
          />
        </StandardForm>
      </>
    );
  };
  return (
    <BoundedContent>
      <GridContainer>
        <Row>
          <Column
            span={12}
            offset={0}
            spanSm={8}
            offsetSm={2}
            spanMd={6}
            offsetMd={3}
            spanLg={4}
            offsetLg={4}
            spanXl={4}
            offsetXl={4}
          >
            {renderContent()}
          </Column>
        </Row>
      </GridContainer>
    </BoundedContent>
  );
};

export default Reset;
